import {
  ArchiosExpressionProfile,
  ExpressionBuilderView,
  ExpressionContextMenuComponent,
  ExpressionTokenComponent,
  StandardExpressionBuilderView,
  Syntax,
  TokenVariable,
} from '@trlogic/ui-pack';
import { Component, Fragment, ReactNode } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { changeStream } from '../../../redux/actions/StreamActions';
import { RootState } from '../../../redux/store';
import { StreamState } from '../../../redux/reducers/StreamReducer';
import FormicaLabel from '../../custom/FormicaLabel';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import CollectionNames from '../../../constant/CollectionNames';
import VariableType from '../../../constant/VariableType';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CustomLoading from '../../custom/CustomLoading';
import DataType from '../../../constant/DataType';
import ExpressionBridge from '../../../util/ExpressionBridge';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import LanguageUtil from '../../../util/LanguageUtil';
import { generateUUIDV4 } from '../../../util/Generator';
import ComponentName from '../../../constant/ComponentName';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import SubscriptionContext = subscribe.SubscriptionContext;
import { ExpressionUtil } from '../../../util/ExpressionUtil';

class StreamKeyComponent extends Component<Props, State> {
  public state = new State();

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);
  private readonly expressionBridge: ExpressionBridge = ServiceContainer.getInstance().get(ServiceNames.EXPRESSION_BRIDGE);

  private computation: subscribe.Computation | undefined;

  public render(): ReactNode {
    const { expressionView, loading } = this.state;
    if (expressionView == undefined || loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col justify-center w-full gap-y-3'} data-testid={`${ComponentName.STREAM}_key`}>
        <FormicaLabel label={LanguageUtil.get({ id: `common.label.key`, defaultMessage: 'Key' })} />
        <div className={'flex flex-col w-full border-[1px] border-[#E0E4E8] rounded-[4px] py-5 bg-main-gray'}>
          <div id='expression_builder_key' data-testid={`${ComponentName.STREAM}_key_expression`}>
            {expressionView && (
              <Fragment>
                <ExpressionTokenComponent syntax={expressionView.getModel()} view={expressionView} />
                <ExpressionContextMenuComponent context={expressionView.getContext()} close={this.onClose} />
              </Fragment>
            )}
          </div>
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    this.build();
    this.computation = subscribe.Tracker.autorun(this.tracker, this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS));
  }

  private tracker = async () => {
    if (this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS).some((s) => s.ready())) {
      this.build();
    }
  };

  private build = async () => {
    if (this.state.expressionView != undefined) return;
    const {
      stream: { stream },
      changeStream,
    } = this.props;
    let key: Syntax = stream.key;
    if (key == undefined || typeof key != 'object') {
      key = { id: generateUUIDV4(), value: { type: 'TokenGroup' }, children: [] };
      stream.key = key;
      changeStream(stream);
    }
    const variables: Record<string, TokenVariable[]> = await this.prepareVariables();
    let expression = await this.expressionBridge.start(key, variables);
    const expressionView: ExpressionBuilderView = new StandardExpressionBuilderView({
      el: '#expression_builder_key',
      model: expression,
      profile: new ArchiosExpressionProfile(),
      returnType: { dataType: DataType.STRING },
      onCtrlToken: ExpressionUtil.onNavigateToken,
    });
    expressionView.getContext().setVariables(variables);
    expressionView.onChange(this.onChangeSyntax as any);
    this.setState({ expressionView, loading: false }, () => {
      expressionView.initialize();
      if (this.props.disabled) expressionView.pause();
    });
  };

  public async componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.update != this.props.update) {
      await this.refreshVariables();
    }
    if (this.props.disabled != prevProps.disabled) {
      if (this.props.disabled) {
        this.state.expressionView?.pause();
      } else {
        this.state.expressionView?.resume();
      }
    }
    if (this.props.streamUpdated != prevProps.streamUpdated) {
      this.state.expressionView?.terminate();
      this.setState({ expressionView: undefined }, () => {
        this.build();
      });
    }
  }

  public componentWillUnmount() {
    const { expressionView } = this.state;
    expressionView?.terminate();
    this.computation?.stop();
  }

  private onChangeSyntax = (updatedExpression: Syntax): void => {
    const {
      stream: { stream },
      changeStream,
    } = this.props;
    stream.key = updatedExpression;
    changeStream(stream);
  };

  private onClose() {}

  private prepareVariables = async (): Promise<Record<string, TokenVariable[]>> => {
    const {
      stream: { stream },
    } = this.props;
    const event: TransactionJsonModel | undefined = await this.repository.find(
      QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('name', stream.transaction)).build(),
    );
    if (event == undefined) return {};
    const eventVariables = event.variables.map((v) => ({
      type: VariableType.EVENT_VARIABLE as any,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.EVENT_VARIABLE,
        properties: v.properties,
        name: v.name,
        description: v.description,
        args: {
          event: event.name,
        },
      },
    }));
    return { [VariableType.EVENT_VARIABLE]: eventVariables };
  };

  private refreshVariables = async () => {
    const { expressionView } = this.state;
    this.setState({ loading: true });
    const variables = await this.prepareVariables();
    expressionView?.getContext().setVariables(variables);
    this.setState({ loading: false });
  };
}

interface Props extends ReduxProps {
  update: number;
  disabled: boolean;
  streamUpdated: number;
}

class State {
  expressionView: ExpressionBuilderView | undefined;
  variables: Record<string, TokenVariable[]> = {};
  loading = true;
}

const mapStateToProps = (state: RootState) => ({
  stream: state.stream as StreamState,
});

const mapDispatchToProps = { changeStream };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(StreamKeyComponent);
